/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Observable } from 'rxjs';
import { PreviewEnvironment } from 'app/shared-data/models';
import { SharedDataService } from './shared-data.service';
import { observableOf } from 'app/shared/rxjs/rxjs.utils';
import { DateTime } from 'luxon';
import { Injectable } from '@angular/core';
import {
  TenantDetailsWithFlags,
  TenantStatus,
  TenantDetails
} from 'app/page-modules/tenant-management/models';
import { delay } from 'rxjs/operators';

/** Mock implementation of SharedData service. */
@Injectable()
export class MockSharedDataService extends SharedDataService {
  allTenantDetails: TenantDetailsWithFlags[] = [
    {
      data: {
        name: 'ProvisioningTest',
        clientId: 'ProvisioningTest-clientId',
        organizationId: 'PR1',
        permissions: [],
        emailDomainsWithConnectionTypes: {},
        history: {},
        status: 'provisioning' as TenantStatus,
        tags: []
      },
      editable: false,
      editableBi: false
    },
    {
      data: {
        name: 'OpenGamma',
        clientId: 'OpenGamma-clientId',
        organizationId: 'og1',
        permissions: ['carma-user', 'carma-calculate', 'carma-portfolio'],
        emailDomainsWithConnectionTypes: {
          'glenpoint.com': 'MAGIC_LINK',
          'thomas+g10@opengamma.com': 'MAGIC_LINK',
          'jonathan+g10@opengamma.com': 'MAGIC_LINK',
          'phuvan+g10@opengamma.com': 'MAGIC_LINK',
          'veeral+g10@opengamma.com': 'MAGIC_LINK',
          'max+g10@opengamma.com': 'MAGIC_LINK'
        },
        history: {
          lch: 2
        },
        status: 'disabled' as TenantStatus,
        tags: ['demo', 'foo', 'bar']
      },
      editable: true,
      editableBi: true
    },
    {
      data: {
        name: 'Glen Point Capital LLP',
        clientId: 'G10-clientId',
        organizationId: 'G10',
        permissions: ['carma-user', 'carma-calculate', 'carma-portfolio'],
        emailDomainsWithConnectionTypes: {
          'joe+g10@opengamma.com': 'MAGIC_LINK',
          'max+g10@opengamma.com': 'MAGIC_LINK',
          'krzysztof+g10@opengamma.com': 'MAGIC_LINK',
          'jonathan+g10@opengamma.com': 'MAGIC_LINK',
          'phuvan+g10@opengamma.com': 'MAGIC_LINK',
          'veeral+g10@opengamma.com': 'MAGIC_LINK',
          'david+g10@opengamma.com': 'MAGIC_LINK',
          'ken+g10@opengamma.com': 'MAGIC_LINK',
          'thomas+g10@opengamma.com': 'MAGIC_LINK',
          'glenpointcapital.com': 'MAGIC_LINK',
          'peter+g10@opengamma.com': 'MAGIC_LINK',
          'roman+g10@opengamma.com': 'MAGIC_LINK',
          'brian+g10@opengamma.com': 'MAGIC_LINK'
        },
        history: {
          eurex: 0,
          jscc: 1,
          cme_span: 2,
          jscc_span: 3,
          euronext_span: 0,
          nse_span: 0,
          idem: 0
        },
        status: 'enabled' as TenantStatus,
        tags: ['prod']
      },
      editable: false,
      editableBi: true
    },
    {
      data: {
        name: 'HSBC (Collateral and Valuation Support)',
        clientId: 'H10-clientId',
        organizationId: 'H10',
        permissions: ['margin-lch', 'margin-cme', 'margin-eurex', 'margin-simm'],
        emailDomainsWithConnectionTypes: {
          'hsbc.fr': 'MAGIC_LINK',
          'hsbc.com': 'MAGIC_LINK',
          'hsbcib.com': 'MAGIC_LINK'
        },
        history: {
          lch: 0,
          simm: 0,
          eurex: 0,
          jscc: 0,
          cme_span: 0,
          jscc_span: 0,
          hkex_span: 0,
          asx_span: 0,
          cdcc_span: 0,
          sgx_span: 0,
          cme_approx: 0,
          ice_span: 0,
          cboe_span: 0,
          euronext_span: 0,
          nse_span: 0,
          tif_span: 0,
          idem: 0
        },
        status: 'enabled' as TenantStatus,
        tags: ['Demo', 'Unexpected1', 'Unexpected2']
      },
      editable: false,
      editableBi: false
    },
    {
      data: {
        name: 'LMR Partners',
        clientId: 'L11-clientId',
        organizationId: 'L11',
        permissions: ['margin-eurex', 'margin-cme_span', 'margin-lch', 'margin-euronext_span'],
        emailDomainsWithConnectionTypes: { 'lmrpartners.com': 'MAGIC_LINK' },
        history: {
          cme_approx: 4,
          ice_span: 5,
          cboe_span: 0
        },
        status: 'disabled' as TenantStatus,
        tags: ['Test']
      },
      editable: false,
      editableBi: false
    }
  ];

  /** Returns available preview environment data. */
  previewEnvironments(): Observable<PreviewEnvironment[]> {
    return observableOf([
      {
        environmentName: 'fooEnvironment',
        modificationInstant: DateTime.fromISO('2019-04-25T10:00:00Z')
      },
      {
        environmentName: 'barEnvironment',
        modificationInstant: DateTime.fromISO('2019-04-18T13:45:15Z')
      }
    ]);
  }

  tenants(): Observable<TenantDetails[]> {
    return observableOf(this.allTenantDetails.map(tenantWithFlag => tenantWithFlag.data)).pipe(
      delay(1000)
    );
  }
}
